import posthog from "posthog-js";
import type { LogAttributes, Properties } from "posthog-js";

const projectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

export const posthogConfigured = Boolean(projectToken && host);

export type PostHogStatus = "pending" | "initialized";

/**
 * Contexto da chamada em andamento. É registrado como super property no PostHog
 * (anexado automaticamente a todos os eventos) e refletido nos logs do Faro.
 */
export type CallContext = {
  callId?: string | null;
  aderenteSlug?: string | null;
  callType?: string | null;
};

const self = {
  status: "pending" as PostHogStatus,

  // Events
  capture: (event: string, properties?: Properties | null) => {
    if (self.status !== "initialized") return;
    posthog.capture(event, properties);
  },

  // Person identity (usuário, nunca a chamada)
  identify: (distinctId: string, properties?: Properties) => {
    if (self.status !== "initialized") return;
    posthog.identify(distinctId, properties);
  },

  reset: () => {
    if (self.status !== "initialized") return;
    posthog.reset();
  },

  // Super properties
  register: (properties: Properties) => {
    if (self.status !== "initialized") return;
    posthog.register(properties);
  },

  unregister: (property: string) => {
    if (self.status !== "initialized") return;
    posthog.unregister(property);
  },

  /**
   * Associa a chamada corrente aos próximos eventos (via super properties).
   * O `callId` entra como propriedade de evento, NÃO como identidade da pessoa.
   */
  setCallContext: (context: CallContext) => {
    if (self.status !== "initialized") return;

    const properties: Properties = {};

    if (context.callId) properties.callId = context.callId;
    if (context.aderenteSlug) properties.aderenteSlug = context.aderenteSlug;
    if (context.callType) properties.callType = context.callType;

    if (Object.keys(properties).length) posthog.register(properties);
  },

  clearCallContext: () => {
    if (self.status !== "initialized") return;

    posthog.unregister("callId");
    posthog.unregister("aderenteSlug");
    posthog.unregister("callType");
  },

  // Logs (PostHog Logs)
  logger: {
    info: (body: string, attributes?: LogAttributes) => {
      if (self.status !== "initialized") return;
      posthog.logger.info(body, attributes);
    },
    warn: (body: string, attributes?: LogAttributes) => {
      if (self.status !== "initialized") return;
      posthog.logger.warn(body, attributes);
    },
    error: (body: string, attributes?: LogAttributes) => {
      if (self.status !== "initialized") return;
      posthog.logger.error(body, attributes);
    },
  },
};

if (!posthogConfigured) {
  if (import.meta.env.DEV) {
    const missingVariable = !projectToken ? "VITE_PUBLIC_POSTHOG_PROJECT_TOKEN" : "VITE_PUBLIC_POSTHOG_HOST";
    console.warn(`[PostHog] ${missingVariable} ausente: eventos do PostHog serão ignorados até a variável ser configurada.`);
  }
} else {
  posthog.init(projectToken, {
    api_host: host,
    defaults: "2026-01-30",
    logs: {
      serviceName: "softphone-web",
      environment: import.meta.env.MODE,
    },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  });

  self.status = "initialized";
}

export default self;
